import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Check, Loader2, Plus, RefreshCcw, Save } from "lucide-react";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordian";

import {
  addCarInZone,
  changeHourlyZoneForm,
  changeZoneStatus,
  removeCarInZone,
} from "../../redux/pricing/slice";

import { cn } from "../../utils/lib";

import { CAR_TYPES } from "../../constants/cars";
import { MODES } from "../../constants/booking-modes";

export function TableHourlyPricing() {
  const dispatch = useDispatch();

  const pricingTableRef = React.useRef(null);
  const inputRefs = React.useRef({}); // Store input element references

  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const token = useSelector(({ auth }) => auth.token);

  const pricing = useSelector(({ pricing }) => pricing);
  const zones = useSelector(({ pricing }) => pricing.zones);
  const cities = useSelector(({ pricing }) => pricing.cities);

  const currentSelectedCityId = useSelector(
    ({ pricing }) => pricing.currentSelectedCityId
  );

  const currentSelectedCity = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId]
  );

  const hourlyZoneIds = useSelector(({ pricing }) =>
    Object.keys(zones).filter(
      (zoneId) =>
        pricing.zones[zoneId].bookingType === "HOURLY" &&
        pricing.zones[zoneId].cityId === currentSelectedCityId
    )
  );

  const hourlyZoneId = hourlyZoneIds[0];

  const { isZoneFetching } = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId]
  );

  const mode = useSelector(({ pricing }) => pricing.newZoneCreationForm.mode);
  const currency = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId].currency
  );

  const areAllCarsSelected = CAR_TYPES.every(
    (CAR) => !!zones[hourlyZoneId]?.pricing?.[CAR.categoryType]
  );

  const handleCreateCity = async () => {
    dispatch({
      type: "EXTRANET_CREATE_ZONE_BY_CITY_ID",
      payload: {
        bookingType: MODES[1],
        currency,
        cityId: currentSelectedCityId,
        newCarCategory: {},
        pricing: {},
        token,
        callbacks: { setIsLoading, setIsError, setIsSuccess: () => {} },
      },
    });
  };

  if (isZoneFetching)
    return (
      <div className="py-16">
        <Loader2 className="mx-auto size-4 animate-spin" />
      </div>
    );

  return (
    <>
      {isZoneFetching && (
        <p className="text-white text-sm animate-pulse">Fetching Data</p>
      )}

      {!isZoneFetching && !hourlyZoneId && (
        <p className="text-sm text-gray-300">
          No Pricing set for hourly in {currentSelectedCity.cityName}
        </p>
      )}

      {hourlyZoneIds.length === 0 && (
        <div className="pt-24 text-center">
          <Button disabled={isLoading} onClick={handleCreateCity}>
            {!isError && !isLoading && <Plus />}
            {isLoading && <Loader2 className="animate-spin" />}
            {isError && <RefreshCcw className="text-red-500" />}
            <span>Set Hourly Price</span>
          </Button>
        </div>
      )}

      {hourlyZoneIds.length !== 0 && (
        <Accordion
          className="border border-[#353c4a] rounded-md p-2 2md:p-4"
          value="hourly"
          type="single"
          collapsible
        >
          <AccordionItem
            className="p-2 md:p-4 border-none"
            value={"hourly"}
            key={"hourly"}
          >
            <AccordionTrigger
              className="pr-1 hover:cursor-default hover:no-underline text-white"
              showChevronIcon={false}
            >
              Houly Pricings for {cities[currentSelectedCityId].cityName}
            </AccordionTrigger>
            <AccordionContent className="space-y-4 overflow-x-scroll pt-4 scrollbar-none">
              <div className="flex flex-wrap gap-4">
                <div className="relative border border-[#353c4a] rounded-md min-w-80 h-80 flex-1 overflow-y-scroll scrollbar-none">
                  <div className="sticky border-b top-0 bg-gradient-to-l from-black via-zinc-800 to-black rounded-tl-lg rounded-tr-lg grid grid-cols-[2rem_1fr] items-center px-4 pl-2 py-3 border-[#353c4a]">
                    <div className="flex w-8 justify-center">
                      {/* Select All Checkbox */}
                      <Checkbox
                        id="select-all-hourly"
                        checked={areAllCarsSelected}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            // Select all cars
                            CAR_TYPES.forEach((CAR) => {
                              dispatch(
                                addCarInZone({
                                  zoneId: hourlyZoneId,
                                  carName: CAR.categoryType,
                                  carPrice: {
                                    hourlyPrice: "0",
                                    minNoOfHours: "0",
                                  },
                                })
                              );
                            });
                          } else {
                            // Unselect all cars
                            CAR_TYPES.forEach((CAR) => {
                              dispatch(
                                removeCarInZone({
                                  zoneId: hourlyZoneId,
                                  carName: CAR.categoryType,
                                })
                              );
                            });
                          }

                          dispatch(
                            changeZoneStatus({
                              zoneId: hourlyZoneId,
                              newStatus: {
                                isChanged: true,
                                isError: false,
                                isDone: false,
                              },
                            })
                          );
                        }}
                        className="mx-auto bg-[#181E29] border-[#4b5563]"
                      />
                    </div>
                    <Label
                      htmlFor="select-all-hourly"
                      className="text-xs xl:text-base font-medium leading-none capitalize text-white"
                    >
                      Car Types
                    </Label>
                  </div>
                  {CAR_TYPES.map((CAR, index) => (
                    <div
                      key={CAR.id}
                      className={cn(
                        "grid grid-cols-[2rem_1fr] items-start px-4 py-2 pl-2",
                        CAR_TYPES.length - 1 !== index &&
                          "border-b border-[#353c4a]"
                      )}
                    >
                      <Checkbox
                        id={CAR.id}
                        className="mx-auto"
                        checked={
                          !!zones[hourlyZoneId]?.pricing?.[CAR.categoryType]
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            dispatch(
                              addCarInZone({
                                zoneId: hourlyZoneId,
                                carName: CAR.categoryType,
                                carPrice: {
                                  hourlyPrice: "0",
                                  minNoOfHours: "0",
                                },
                              })
                            );
                            setTimeout(() => {
                              pricingTableRef.current?.scrollTo({
                                top: 0,
                                behavior: "smooth",
                              });
                            }, 100);
                            setTimeout(() => {
                              inputRefs.current[
                                `${CAR.categoryType}-price`
                              ]?.focus();
                            }, 300);
                          } else {
                            dispatch(
                              removeCarInZone({
                                zoneId: hourlyZoneId,
                                carName: CAR.categoryType,
                              })
                            );
                          }

                          dispatch(
                            changeZoneStatus({
                              zoneId: hourlyZoneId,
                              newStatus: {
                                isChanged: true,
                              },
                            })
                          );
                        }}
                      />
                      <Label htmlFor={CAR.id} className="hover:cursor-pointer">
                        <p className="text-base font-medium capitalize leading-none text-white ">
                          {CAR.categoryType}
                        </p>
                        <p className="text-xs font-light leading-4 text-white">
                          {CAR.description}
                        </p>
                      </Label>
                    </div>
                  ))}
                </div>

                <div
                  ref={pricingTableRef}
                  className="relative border border-[#353c4a] rounded-md min-w-80 h-80 flex-1 overflow-y-scroll scrollbar-none"
                >
                  <div className="sticky border-b top-0 bg-gradient-to-l from-black via-zinc-800 text-white to-black rounded-tl-lg rounded-tr-lg grid grid-cols-3 items-center px-4 pl-2 py-3 border-[#353c4a]">
                    <p className="text-sm font-medium capitalize leading-none">
                      Selected Car Types
                    </p>
                    <p className="mx-auto text-sm">Hourly Price</p>
                    <p className="mx-auto text-sm">Min no.of hours</p>
                  </div>
                  {Object.keys(zones[hourlyZoneId].pricing ?? {}).map(
                    (CAR, index) => (
                      <div
                        key={CAR}
                        className={cn(
                          " grid grid-cols-[1fr_1fr_1fr] items-center gap-x-1 px-4 py-2 pl-2",
                          CAR_TYPES.length - 1 !== index &&
                            "border-b border-[#353c4a]"
                        )}
                      >
                        <div>
                          <p className="text-xs font-medium capitalize leading-none text-white">
                            {CAR}
                          </p>
                        </div>
                        <div className="mx-auto">
                          <Input
                            className="bg-[#181E29] border border-[#353c4a] block w-full truncate ring-1 ring-transparent placeholder:truncate focus:outline-none focus-visible:border disabled:border disabled:opacity-90 disabled:bg-gray-100 disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-black text-center text-white focus:border-none"
                            type="number"
                            value={
                              zones[hourlyZoneId]?.pricing?.[CAR]
                                ?.hourlyPrice ?? "0"
                            }
                            min={0}
                            onChange={(e) => {
                              dispatch(
                                changeHourlyZoneForm({
                                  zoneId: hourlyZoneId,
                                  carName: CAR,
                                  carPrice: {
                                    hourlyPrice: e.target.value,
                                    minNoOfHours:
                                      zones[hourlyZoneId]?.pricing?.[
                                        CAR
                                      ]?.minNoOfHours?.toString() ?? "0",
                                  },
                                })
                              );
                              dispatch(
                                changeZoneStatus({
                                  zoneId: hourlyZoneId,
                                  newStatus: {
                                    isChanged: true,
                                  },
                                })
                              );
                            }}
                            ref={(el) => {
                              inputRefs.current[`${CAR}-price`] = el;
                            }}
                          />
                        </div>
                        <div className="mx-auto">
                          <Input
                            className="bg-[#181E29] border border-[#353c4a] block w-full truncate ring-1 ring-transparent placeholder:truncate focus:outline-none focus-visible:border disabled:border disabled:opacity-90 disabled:bg-gray-100 disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-black text-center text-white focus:border-none"
                            type="number"
                            value={
                              zones[hourlyZoneId]?.pricing?.[CAR]
                                ?.minNoOfHours ?? "0"
                            }
                            min={0}
                            onChange={(e) => {
                              dispatch(
                                changeHourlyZoneForm({
                                  zoneId: hourlyZoneId,
                                  carName: CAR,
                                  carPrice: {
                                    hourlyPrice:
                                      zones[hourlyZoneId]?.pricing?.[
                                        CAR
                                      ]?.hourlyPrice?.toString() ?? "0",
                                    minNoOfHours: e.target.value,
                                  },
                                })
                              );
                              dispatch(
                                changeZoneStatus({
                                  zoneId: hourlyZoneId,
                                  newStatus: {
                                    isChanged: true,
                                  },
                                })
                              );
                            }}
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                {!pricing.zoneStatus[hourlyZoneId].isChanged && (
                  <Button
                    variant={"outline"}
                    type="button"
                    className="bg-transparent hover:bg-transparent border-[#353c4a]"
                  >
                    <Check className="text-green-500" />
                    <span className="text-white ">Up to date</span>
                  </Button>
                )}
                {pricing.zoneStatus[hourlyZoneId].isChanged && (
                  <Button
                    type="button"
                    onClick={() => {
                      dispatch({
                        type: "EXTRANET_UPDATE_ZONE_PRICNG_BY_ID",
                        payload: {
                          zoneId: hourlyZoneId,
                        },
                      });
                    }}
                  >
                    {pricing.zoneStatus[hourlyZoneId].isLoading && (
                      <Loader2 className="animate-spin" />
                    )}
                    {pricing.zoneStatus[hourlyZoneId].isError && (
                      <RefreshCcw className="text-red-500" />
                    )}
                    {!pricing.zoneStatus[hourlyZoneId].isLoading &&
                      !pricing.zoneStatus[hourlyZoneId].isError && <Save />}
                    <span className="hidden sm:block">Save Pricing</span>
                  </Button>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </>
  );
}
